import React, { useEffect, useRef, useState } from "react";

import {
    Dimensions,
    Platform,
    TouchableOpacity,
    View,
    StyleSheet
} from "react-native";
import AppTheme, { dark_blue, dark_purple, dark_yellow, purple } from "../../../shared/utils/Theme";
import Text from "../../../shared/components/ReactNative/Text";
import { Offerings, Package } from "@revenuecat/purchases-js";
import User from "../../../shared/data/user/User";
import ActivityIndicator from "../../../shared/designSystem/ActivityIndicator/ActivityIndicator";
import { IcCheck } from "../../../shared/utils/Icons";
import Button from "../../../shared/designSystem/Button";
import { translate } from "../../../shared/utils/Localization/Localization";
import LinkButton from "../../../shared/designSystem/Buttons/LinkButton";
import { analytcisEventPaywallOfferClicked, propertiesEventAmount, propertiesEventDuration, propertiesEventProfile, trackEvent } from "../../../shared/services/Tracking/Analytics";
import { treatPurchaserInfo } from "../../services/InAppPurchase/InAppPurchaseService";
import Fonts from "../../../shared/Theme/Fonts";
import SVGView from "../../../shared/utils/SvgView";
import Alert from "../../../shared/components/Notification/Alert";
import Divider from "../../../shared/designSystem/Divider/Divider";
import { openUrl } from "../../../shared/utils/WebLink";
import { ensurePurchasesUser } from "../../services/InAppPurchase/RevenueCat";


interface PremiumSubBlockProps {
    onPlanSelection: (plan: any) => void;
    onSubscriptionConfirmed: () => void;
    screenSize?: number;
}

const PremiumSubBlock = (props: PremiumSubBlockProps) => {

    const { onPlanSelection, onSubscriptionConfirmed, screenSize } = props;

    const appTheme: AppTheme = new AppTheme();
    const user: User = User.getInstance();

    const mainColor: string = user.profile === "family" ? dark_yellow : dark_purple;
    const mainTextColor: string = user.profile === "family" ? dark_blue : "#FFFFFF";
    const selectionColor: string = user.profile === "family" ? dark_blue : dark_yellow;

    const alertRef = useRef<Alert>(null);

    // Définition de la largeur des blocks
    let blocksWidth: number = screenSize !== undefined ? screenSize - appTheme.pixelPerfect(20) : appTheme.getBlockWidth() - appTheme.pixelPerfect(20);

    const [offering, setOffering] = useState<Package[] | null>(null);
    const [planSelected, setPlanSelected] = useState<Package | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [purchases, setPurchases] = useState<Awaited<ReturnType<typeof ensurePurchasesUser>> | null>(null);

    useEffect(() => {
        let active = true;
        ensurePurchasesUser(user.uid)
            .then(instance => {
                if (active) setPurchases(instance);
            })
            .catch(error => {
                console.error("Impossible d'initialiser RevenueCat", error);
                if (active) setOffering([]);
            });
        return () => {
            active = false;
        };
    }, [user.uid]);

    useEffect(() => {
        if (purchases !== null && offering === null) {
            fecthCurrentOfferings().catch(error => {
                console.error("Impossible de charger les offres RevenueCat", error);
                setOffering([]);
            });
        }
    }, [purchases, offering]);

    const fecthCurrentOfferings = async () => {
        if (purchases === null) return;
        const currentOfferings: Offerings = await purchases.getOfferings();
        const offeringsToDisplay: string = user.profile === "family" ? "LAHV2" : "LASV2";
        if ((currentOfferings.all !== undefined) && (currentOfferings.all[offeringsToDisplay] !== undefined) && (currentOfferings.all[offeringsToDisplay].availablePackages !== undefined)) {
            let liliPackages: any[] = [];
            for (const aPackageIndex in currentOfferings.all[offeringsToDisplay].availablePackages) {
                if (!Object.hasOwn(currentOfferings.all[offeringsToDisplay].availablePackages, aPackageIndex)) continue;

                const anOffer: Package = currentOfferings.all[offeringsToDisplay].availablePackages[aPackageIndex];
                let offerInserted: boolean = false;
                for (const anOrderedIndex in liliPackages) {
                    if (!Object.hasOwn(liliPackages, anOrderedIndex)) continue;

                    const anOrderedOffer: Package = liliPackages[anOrderedIndex];
                    let anOfferPricePerMonth: number = anOffer.webBillingProduct.price.amountMicros;
                    if (anOffer.webBillingProduct.period?.unit === "year") {
                        anOfferPricePerMonth = anOfferPricePerMonth / (12 * anOffer.webBillingProduct.period.number);
                    } else if (anOffer.webBillingProduct.period?.unit === "month") {
                        anOfferPricePerMonth = anOfferPricePerMonth / anOffer.webBillingProduct.period.number;
                    }

                    let anOrderedOfferPricePerMonth: number = anOrderedOffer.webBillingProduct.price.amountMicros;
                    if (anOrderedOffer.webBillingProduct.period?.unit === "year") {
                        anOrderedOfferPricePerMonth = anOrderedOfferPricePerMonth / (12 * anOrderedOffer.webBillingProduct.period.number);
                    } else if (anOrderedOffer.webBillingProduct.period?.unit === "month") {
                        anOrderedOfferPricePerMonth = anOrderedOfferPricePerMonth / anOrderedOffer.webBillingProduct.period.number;
                    }

                    if (anOfferPricePerMonth > anOrderedOfferPricePerMonth) {
                        liliPackages.splice(parseInt(anOrderedIndex), 0, anOffer);
                        offerInserted = true;
                        break;
                    }

                }
                if (offerInserted === false) {
                    liliPackages.push(anOffer);
                }
            }
            setOffering(liliPackages);
            setPlanSelected(liliPackages[liliPackages.length - 1]);
        }
    }

    const renderLegalLineOnSubscription = (): string => {
        if (planSelected === null) {
            return "";
        } else {
            if (planSelected.webBillingProduct.productType === "subscription") {
                return "Abonnement sans engagement. Résiliable à tout moment.";
            } else {
                return "La licence Premium s'arrête automatiquement à la fin de la période.";
            }
        }
    }

    const renderRestoreButtonTitle = (): string => {
        if (planSelected === null) {
            return "";
        } else {
            if (planSelected.webBillingProduct.productType === "subscription") {
                return "Je suis déjà abonné(e)";
            } else {
                return "J'ai déjà une licence";
            }
        }
    }

    const plansCell = (plan: { offer: any, id: string; title: string; price: string; period: string | null; firstWeekFree: boolean; promotion?: number; }) => {
        const { id, offer, title, price, period, promotion, firstWeekFree } = plan;

        let isPlanSelected: boolean = (planSelected !== null) && (planSelected.identifier === id);
        if (planSelected === null) {
            if (id === "$rc_annual") {
                isPlanSelected = true;
            }
        }
        let cellSize: number = blocksWidth * 0.45;
        let backgroundColor: string = "#FFFFFF";
        let textColor: string = mainTextColor;
        let fontSize: number = appTheme.pixelPerfectForFont(7);
        let checkIconView: React.JSX.Element | null = null;
        let checkIconViewBg: string = "#FFFFFF";
        if (isPlanSelected === true) {
            cellSize = cellSize * 1;
            backgroundColor = mainColor;
            textColor = mainTextColor;
            fontSize = appTheme.pixelPerfectForFont(8);
            checkIconView = <SVGView Component={IcCheck} color={"#FFFFFF"} width={10} height={10} />;
            checkIconViewBg = selectionColor;
        }
        const promotionBadge: React.JSX.Element | null = firstWeekFree === false ? null : <View style={{ position: "absolute", top: 0, right: appTheme.pixelPerfect(50), height: appTheme.pixelPerfect(20), borderRadius: appTheme.pixelPerfect(6), backgroundColor: dark_blue, justifyContent: "center", alignItems: "center" }}>
            <Text style={{ color: "#FFFFFF", fontFamily: appTheme.primaryBoldFont, fontSize: appTheme.pixelPerfectForFont(4), marginHorizontal: appTheme.pixelPerfect(10), textAlign: "center" }}>{`1 semaine d'essai offerte`}</Text>
        </View>;
        let anOfferPricePerMonth: number = offer.webBillingProduct.price.amountMicros;
        let anOfferTitle: string = "Mensuel";
        let isAlreadyAnOfferForOneMonth: boolean = true;
        if (user.profile === "teacher") {
            anOfferTitle = "Licence enseignant";
        } else {
            if (offer.webBillingProduct.period?.unit === "year") {
                anOfferPricePerMonth = anOfferPricePerMonth / (12 * offer.webBillingProduct.period.number);
                isAlreadyAnOfferForOneMonth = false;
                anOfferTitle = "Annuel";
            } else if (offer.webBillingProduct.period?.unit === "month") {
                anOfferPricePerMonth = anOfferPricePerMonth / offer.webBillingProduct.period.number;
                if (offer.webBillingProduct.period.number > 1) {
                    isAlreadyAnOfferForOneMonth = false;
                    anOfferTitle = offer.webBillingProduct.period.number === 2 ? "Bimensuel" : offer.webBillingProduct.period.number === 3 ? "Trimestriel" : "Semestriel";
                }
            }
        }

        anOfferPricePerMonth = anOfferPricePerMonth / 1000000;
        const priceByMonth: React.JSX.Element | null = isAlreadyAnOfferForOneMonth === false ? <Text style={{ width: "100%", textAlign: "right", color: textColor, fontFamily: appTheme.primaryMediumFont, fontSize: appTheme.pixelPerfectForFont(5) }}>{`soit ${anOfferPricePerMonth.toFixed(2)} /mois`}</Text> : null;
        let priceToDisplay: string = `${price}`;
        if (period !== null) {
            priceToDisplay = `${priceToDisplay} ${period}`;
        } else if (offer.identifier === "$rc_annual") {
            priceToDisplay = `${priceToDisplay}\npour 12 mois`;
        }
        return <TouchableOpacity onPress={() => setPlanSelected(offer)} style={{ marginTop: appTheme.pixelPerfect(10), height: appTheme.pixelPerfect(100), justifyContent: "flex-end", width: cellSize, alignItems: "center" }}>
            <View style={{ height: appTheme.pixelPerfect(90), paddingBottom: 0, overflow: "hidden", borderWidth: appTheme.pixelPerfect(1), borderColor: mainColor, width: cellSize, borderRadius: 14, backgroundColor: backgroundColor, justifyContent: "center", alignItems: "flex-start" }}>
                <View style={{ width: cellSize, paddingHorizontal: 10, flexDirection: "row", justifyContent: "center", alignItems: "center", height: appTheme.pixelPerfect(30) }}>
                    <View style={{ justifyContent: "center", alignItems: "center", borderWidth: 1, borderColor: mainColor, backgroundColor: checkIconViewBg, width: appTheme.pixelPerfect(16), height: appTheme.pixelPerfect(16), borderRadius: appTheme.pixelPerfect(8) }}>
                        {checkIconView}
                    </View>
                    <View style={{ flex: 1, alignItems: "flex-start" }}>
                        <Text style={{ paddingStart: appTheme.pixelPerfect(5), textAlign: "left", color: mainTextColor, fontFamily: appTheme.primaryBoldFont, fontSize }}>
                            {anOfferTitle.toLocaleUpperCase()}
                        </Text>
                    </View>
                    <View>
                        <Text style={{ width: "100%", textAlign: "right", color: textColor, fontFamily: appTheme.primaryBoldFont, fontSize }}>
                            {`${priceToDisplay}`}
                        </Text>
                        {priceByMonth}
                    </View>
                </View>
            </View>
            {promotionBadge}
        </TouchableOpacity>
    }

    const onRestoreRequest = async () => {
        try {
            setIsLoading(true);
            if (purchases === null) throw new Error("RevenueCat n'est pas initialisé.");
            const customerInfo = await purchases.getCustomerInfo();
            treatPurchase(customerInfo, true);
        } catch (error) {
            console.error(
                "Impossible de récupérer les achats RevenueCat",
                error
            );
            setIsLoading(false);
        }
    };

    const onPurchaseRequest = async () => {
        setIsLoading(true);
        try {
            if (planSelected !== null && purchases !== null) {
                trackEvent(
                    analytcisEventPaywallOfferClicked,
                    {
                        [propertiesEventAmount]: planSelected.webBillingProduct.price.amountMicros / 1000,
                        [propertiesEventDuration]: planSelected.packageType,
                        [propertiesEventProfile]: user.userProfile()
                    }
                );
                await purchases.purchase({
                    rcPackage: planSelected,
                    customerEmail: user.email
                });
                setIsLoading(false);
                onSubscriptionConfirmed();
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            console.log({ error });
            setIsLoading(false);
        }
    }

    const treatPurchase = (customerInfo: any, isRestoreRequest?: boolean) => {
        const activeSub = treatPurchaserInfo(customerInfo);
        if (activeSub !== null) {
            user.addSubToUser(activeSub);
            setTimeout(() => {
                onSubscriptionConfirmed();
                setIsLoading(false);
            }, 1000);
        } else {
            if (isRestoreRequest === true) {
                alertRef.current?.showAlert({
                    title: "Nous n'avons pas trouvé d'abonnement pour votre compte",
                    body: "Si vous pensez qu'il s'agit d'une erreur, n'hésitez pas à nous contacter : support@lili.cool"
                });
            }
            setIsLoading(false);
        }
    }

    const renderPlans = () => {
        if ((user.profile !== "teacher") && (user.profile !== "family")) {
            return null;
        }
        if (offering === null) {
            return <ActivityIndicator loading={true} color={dark_blue} />
        }
        let plansToDisplay: React.JSX.Element[] = [];
        for (const aPackage of offering) {
            plansToDisplay.push(plansCell({
                offer: aPackage,
                id: aPackage.identifier,
                title: aPackage.webBillingProduct.title,
                price: aPackage.webBillingProduct.price.formattedPrice,
                period: aPackage.webBillingProduct.period === null ? null : aPackage.webBillingProduct.period.unit === "year" ? "/an" : "/mois",
                firstWeekFree: aPackage.webBillingProduct.freeTrialPhase !== null
            }))
        }
        return <View style={{ alignItems: "center", justifyContent: "space-evenly", width: blocksWidth, flexDirection: "row" }}>
            {plansToDisplay}
        </View>
    }

    const onContactRequest = () => {
        openUrl("https://www.lili.cool/fr/contact/");
    }


    const renderSubscribeButton = (): React.JSX.Element | null => {
        if (offering === null) {
            return null;
        }
        let actionLinkToButton: () => void = onPurchaseRequest;
        let buttonTitle: string = ((planSelected !== null) && (planSelected.webBillingProduct.introPricePhase === null)) ? translate.labels().premium.family.plans.details.buttons.no_free_week : translate.labels().premium.family.plans.details.buttons.free_week;
        if (user.profile === "animator") {
            actionLinkToButton = onContactRequest;
            buttonTitle = "Nous contacter";
        }
        return <View style={{ width: blocksWidth, paddingHorizontal: appTheme.pixelPerfect(10), justifyContent: "flex-end", marginTop: appTheme.pixelPerfect(10) }}>
            <Button title={buttonTitle} onPress={actionLinkToButton} textStyle={{ paddingHorizontal: appTheme.pixelPerfect(20), color: appTheme.darkBlue, fontSize: Platform.OS === "web" ? appTheme.pixelPerfectForFont(7) : appTheme.pixelPerfectForFont(8) }} style={{ minHeight: appTheme.pixelPerfect(36), paddingVertical: appTheme.pixelPerfect(4), backgroundColor: "#FFD77A" }} />
        </View>
    }

    const renderLegalData = (): React.JSX.Element | null => {
        if ((user.profile !== "teacher") && (user.profile !== "family")) {
            return null;
        }
        if (offering === null) {
            return null;
        }
        return <Text key="plan_legal" style={{ width: blocksWidth, textAlign: "center", color: appTheme.darkBlue, fontFamily: appTheme.primaryFont, fontSize: appTheme.pixelPerfectForFont(5), marginTop: appTheme.pixelPerfect(5), marginBottom: appTheme.pixelPerfect(10) }}>
            {renderLegalLineOnSubscription()}
        </Text>
    }

    const renderLoadingView = () => {
        if (isLoading === false) {
            return null;
        }
        return <View style={{ width: window.innerWidth, height: window.innerHeight, position: "absolute", backgroundColor: "#FFFFFF80" }}>
            <ActivityIndicator loading={true} color={dark_blue} />
        </View>
    }

    return <View style={{ width: blocksWidth, marginVertical: appTheme.pixelPerfect(10), alignItems: "center" }}>
        {renderPlans()}
        {renderSubscribeButton()}
        <Divider />
        {renderLegalData()}
        <LinkButton textStyle={{ fontFamily: Fonts.primary.semibold }} onPress={onRestoreRequest} title={renderRestoreButtonTitle()} />
        <Alert ref={alertRef} />
        {renderLoadingView()}
    </View>
}

export default PremiumSubBlock;
